import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Upload04Icon, VerifiedIcon } from "../../../assets/icons";
import { Star } from "lucide-react";
import StatusChip from "../../../components/statusChip/StatusChip";
import CommonButton from "../../../components/commonButton";
import { useToast } from "../../../context/TostContext";
import EditCompanyProfileModal from "../../../components/modals/company/EditCompanyProfileModal";
import { exportAllCompanies, fetchCompanyDetail, updateCompanyDetails, setCompanyListSettings, fetchCompanyCategories } from "../../../redux/slice/companySlice";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { usePermission } from "../../../hooks/usePermission";
import CustomTable from "../../../components/table/CustomTable";
import { useFilterDrower } from "../../../context/FilterDrowerContext";
import CompanyFilterDrawer from "./CompanyFilterDrawer";
import InputField from "../../../components/form/InputField";
import { createPortal } from "react-dom";
import { FilterBtn, ResetBtn } from "../../../components/commonButton/btns";
import { TableSearch } from "../../../components/form/Fields";
import { UserInital } from "../../../utils/otherHelopers";
import CustomSelect from "../../../components/form/CustomSelect";
import { CheckboxOption, MultiValueMessage } from "../../../components/form/utils";

const customStyles = {
    menu: (provided) => ({
        ...provided,
        minWidth: "250px",
        zIndex: 9999
    }),
    multiValueRemove: () => ({
        display: 'none',
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "12px",
        backgroundColor: state.isSelected
            ? "transparent"
            : state.isFocused
                ? "var(--primary-100)"
                : "transparent",
        color: 'var(--text-primary)',
        ':active': {
            backgroundColor: "var(--primary-100)"
        }
    })
};




const getVerificationStatusBg = (status) => {
    switch (status) {
        case "approved": return "success";
        case "rejected": return "danger";
        case "revoked": return "warning";
        case "expired": return "secondary";
        case "submitted": return "blue";
        case "pending": return "blue";
        default: return "blue";
    }
};

const getVerificationStatusTitle = (status) => {
    switch (status) {
        case "approved": return "VERIFIED";
        case "rejected": return "REJECTED";
        case "revoked": return "PENDING";
        case "expired": return "EXPIRED";
        case "submitted": return "SUBMITTED";
        case "pending": return "IN PROGRESS";
        default: return "IN PROGRESS";
    }
};

const MODULE = "company_management";

const ListSection = ({ searchTerm, setSearchTerm, currentPage, isLoading,
    isInitialLoad, setCurrentPage, cardStatus, limit, data,
    selectedCities, setSelectedCities, selectedCategories, setSelectedCategories,
    advancedFilters, setAdvancedFilters,
    totalCompanies, handleLimitChange }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToast();
    const { updateCompanyLoading, companyDetail } = useSelector((state) => state.company);
    const { hasPermission } = usePermission();

    const { openFilterDrower, closeFilterDrower } = useFilterDrower();
    const filterStateRef = useRef(advancedFilters || {});

    useEffect(() => {
        filterStateRef.current = advancedFilters;
    }, [advancedFilters]);

    const { cities = [] } = useSelector((state) => state.location || {});
    const { companyCategories } = useSelector((state) => state.company || {});

    const cityOptions = cities.map(c => ({ value: c.id, label: c.name }));
    const categoryOptions = (companyCategories?.data || []).map(c => ({ value: c.id, label: c.name }));

    const [inlineVerification, setInlineVerification] = useState(advancedFilters?.verification_status || "all");
    const [inlineStatus, setInlineStatus] = useState(advancedFilters?.status || "all");
    const [createdDateRange, setCreatedDateRange] = useState({
        start: advancedFilters?.start_date || null,
        end: advancedFilters?.end_date || null
    });

    useEffect(() => {
        setInlineVerification(advancedFilters?.verification_status || "all");
        setInlineStatus(advancedFilters?.status || "all");
        setCreatedDateRange({
            start: advancedFilters?.start_date || null,
            end: advancedFilters?.end_date || null
        });
    }, [advancedFilters]);

    const citySearchTimeoutRef = useRef(null);
    const categorySearchTimeoutRef = useRef(null);

    const handleCitySearch = (inputValue, { action }) => {
        if (action === "input-change") {
            if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
            citySearchTimeoutRef.current = setTimeout(() => {
                dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
            }, 400);
        } else if (action === "menu-close") {
            dispatch(fetchActiveCities({ limit: 20 }));
        }
    };

    const handleCategorySearch = (inputValue, { action }) => {
        if (action === "input-change") {
            if (categorySearchTimeoutRef.current) clearTimeout(categorySearchTimeoutRef.current);
            categorySearchTimeoutRef.current = setTimeout(() => {
                dispatch(fetchCompanyCategories({ search: inputValue, limit: 20 }));
            }, 400);
        } else if (action === "menu-close") {
            dispatch(fetchCompanyCategories({ limit: 20 }));
        }
    };

    const mergedCityOptions = [
        ...(selectedCities || []),
        ...cityOptions.filter(c => !(selectedCities || []).some(sc => sc.value === c.value))
    ];

    const mergedCategoryOptions = [
        ...(selectedCategories || []),
        ...categoryOptions.filter(c => !(selectedCategories || []).some(sc => sc.value === c.value))
    ];

    const getActiveFilterCount = () => {
        let count = 0;
        if (selectedCities?.length > 0) count += 1;
        if (selectedCategories?.length > 0) count += 1;
        if (advancedFilters?.verification_status) count += 1;
        if (advancedFilters?.status) count += 1;
        if (advancedFilters?.min_followers || advancedFilters?.max_followers) count += 1;
        if (advancedFilters?.min_posts || advancedFilters?.max_posts) count += 1;
        if (advancedFilters?.start_date || advancedFilters?.end_date) count += 1;
        return count;
    };

    const filterCount = getActiveFilterCount();
    const isFilterActive = filterCount > 0;

    const handleOpenFilterDrawer = () => {
        const content = (
            <CompanyFilterDrawer
                initialFilters={advancedFilters}
                onChange={(newFilters) => {
                    filterStateRef.current = newFilters;
                }}
            />
        );

        openFilterDrower(content, {
            title: "Filters",
            onClear: () => {
                setAdvancedFilters({});
                setSelectedCities([]);
                setSelectedCategories([]);
                setInlineVerification("all");
                setInlineStatus("all");
                setCreatedDateRange({ start: null, end: null });
                setCurrentPage(1);
                closeFilterDrower();
            },
            onSubmit: () => {
                setAdvancedFilters(filterStateRef.current);

                const newCities = cityOptions.filter(c => filterStateRef.current.city_id?.includes(c.value));
                const newCategories = categoryOptions.filter(c => filterStateRef.current.category_id?.includes(c.value));
                setSelectedCities(newCities);
                setSelectedCategories(newCategories);

                setInlineVerification(filterStateRef.current.verification_status || "all");
                setInlineStatus(filterStateRef.current.status || "all");
                setCreatedDateRange({
                    start: filterStateRef.current.start_date || null,
                    end: filterStateRef.current.end_date || null
                });

                setCurrentPage(1);
                closeFilterDrower();
            },
        });
    };

    const handleResetFilters = () => {
        setInlineVerification("all");
        setInlineStatus("all");
        setCreatedDateRange({ start: null, end: null });
        setSearchTerm("");
        setAdvancedFilters({});
        setSelectedCities([]);
        setSelectedCategories([]);
        setCurrentPage(1);
    };

    const [isExporting, setIsExporting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editLoadingId, setEditLoadingId] = useState(null);

    const handleEditClick = async (item) => {
        setEditLoadingId(item.id);
        try {
            await dispatch(fetchCompanyDetail(item.id)).unwrap();
            setShowEditModal(true);
        } catch {
            toast.error("Failed to load company details");
        } finally {
            setEditLoadingId(null);
        }
    };

    const handleUpsertCompanyProfile = (data) => {
        if (!companyDetail?.id) return;
        dispatch(updateCompanyDetails({ id: companyDetail.id, data }))
            .unwrap()
            .then(() => {
                toast.success("Company profile updated successfully");
                setShowEditModal(false);
            })
            .catch((err) => toast.error(err?.message || "Failed to update company profile"));
    };
    const handleStatusChange = (item, newStatus) => {
        const formData = new FormData();
        formData.append("status", newStatus);

        dispatch(updateCompanyDetails({ id: item.id, data: formData }))
            .unwrap()
            .then(() => {
                toast.success(`Company status updated to ${newStatus}`);
            })
            .catch((err) => toast.error(err?.message || "Failed to update status"));
    };

    const actionList = [
        {
            title: "View",
            handler: (item) => {
                dispatch(setCompanyListSettings({ scrollPosition: window.scrollY }));
                navigate(`/company/${item.id}`);
            }
        },
        {
            title: (item) => editLoadingId === item?.id ? "Loading..." : "Edit",
            isVisible: () => hasPermission(MODULE, "update"),
            handler: (item) => handleEditClick(item)
        },
        {
            title: "Inactive",
            isVisible: (item) => hasPermission(MODULE, "update") && item?.status === "active",
            handler: (item) => handleStatusChange(item, "inactive")
        },
        {
            title: "Active",
            isVisible: (item) => hasPermission(MODULE, "update") && item?.status !== "active",
            handler: (item) => handleStatusChange(item, "active")
        }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB"); // dd-mm-yyyy
    };

    const columns = [
        {
            id: "company_name",
            header: "NAME",
            cell: (_, item) => (
                <div className="d-flex align-items-center gap-2">
                    <UserInital image={item.profile?.logo?.url} name={item.name} />
                    <div className="d-flex align-items-center gap-1">
                        <span>{item.name}</span>
                        {item.is_verified && (
                            <VerifiedIcon size={16} className="text-success ms-1" />
                        )}
                        {item.is_star && (
                            <Star size={16} className="ms-1 text-warning" />
                        )}
                    </div>

                </div>
            )
        },
        {
            id: "owner_details",
            header: "OWNER DETAILS",
            accessor: 'creator',
            cell: (v) => (
                <div className="d-flex align-items-center gap-2">
                    <div className="profile-icon">
                        <img
                            src={v?.profile?.profile_photo?.url || "/images/AllUser/UserImage.jpg"}
                            alt="owner"
                            style={{ objectFit: "cover", width: "100%", height: "100%" }}
                        />
                    </div>
                    <div>
                        <h6 className="fw-semibold fs-md">{v ? `${v.first_name} ${v.last_name || ""}`.trim() : "-"}</h6>
                        <span className="text-muted fs-sm">{v?.mobile_number || "-"}</span>
                        <p className="text-muted fs-sm">{v?.email || "-"}</p>
                    </div>
                </div>
            )
        },
        {
            id: "company_mobile",
            header: "MOBILE NUMBER",
            accessor: 'profile.mobile_number',
        },
        {
            id: "company_email",
            header: "EMAIL ADDRESS",
            accessor: 'profile.email',
        },
        {
            id: "category",
            header: "CATEGORY",
            accessor: 'category.name',
        },
        {
            id: "city",
            header: "CITY",
            accessor: 'profile.city',
            cell: (v) => typeof v === 'string' ? v : v?.name || "-"
        },
        {
            id: "followers_count",
            header: "FOLLOWERS",
            accessor: 'followers_count',
        },
        {
            id: "posts_count",
            header: "POSTS",
            accessor: 'posts_count',
        },
        {
            id: "verification_status",
            header: "VERIFICATION STATUS",
            accessor: 'verification',
            cell: (v) => (
                <StatusChip
                    pill
                    bg={!v ? "warning" : getVerificationStatusBg(v.overall_status)}
                    title={!v ? "PENDING" : getVerificationStatusTitle(v.overall_status)}
                />
            )
        },
        {
            id: "status",
            header: "STATUS",
            accessor: 'status',
            cell: (v) => (
                <StatusChip
                    pill
                    bg={v === "active" ? "success" : "danger"}
                    title={v?.toUpperCase() || "INACTIVE"}
                />
            )
        },
        {
            id: "created_at",
            header: "CREATED AT",
            accessor: 'created_at',
            cell: (v) => formatDate(v)
        },
        {
            id: "updated_at",
            header: "UPDATED AT",
            accessor: 'updated_at',
            cell: (_, item) => formatDate(item.updated_at)
        }
    ];

    const pagination = {
        currentPage,
        totalPages: Math.ceil(totalCompanies / limit),
        total: totalCompanies,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit,
        setPageSize: (newLimit) => handleLimitChange({ value: newLimit, label: newLimit }),
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                All Companies
            </h4>
            <div className="action-buttons-container d-flex align-items-center gap-3">
                {hasPermission(MODULE, "export") && (
                    <CommonButton
                        icon={<Upload04Icon strokeWidth="2.0" />}
                        variant="secondary"
                        className=""
                        title={isExporting ? "Exporting..." : "Export"}
                        type="button"
                        disabled={isExporting}
                        isLoading={isExporting}
                        onClick={async () => {
                            setIsExporting(true);
                            try {
                                await dispatch(exportAllCompanies({
                                    search: searchTerm,
                                    verification_status: cardStatus || advancedFilters.verification_status,
                                    status: advancedFilters.status,
                                    category_ids: selectedCategories?.map((c) => c.value) || [],
                                    city_ids: selectedCities?.map((c) => c.value) || [],
                                    min_followers: advancedFilters.min_followers,
                                    max_followers: advancedFilters.max_followers,
                                    min_posts: advancedFilters.min_posts,
                                    max_posts: advancedFilters.max_posts,
                                    start_date: advancedFilters.start_date,
                                    end_date: advancedFilters.end_date
                                })).unwrap();
                            } catch (err) {
                                console.error("Export failed:", err);
                            } finally {
                                setIsExporting(false);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            {/* table fiter section */}
            <div className="p-3 d-flex flex-wrap align-items-end column-gap-3 row-gap-2 bg-white">
                {/* Search */}
                <TableSearch
                    value={searchTerm}
                    placeholder="Search by name..."
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }} onClear={() => {
                        setSearchTerm("");
                        setCurrentPage(1);
                    }} />

                {/* Verification Status */}
                <CustomSelect
                    label={{ name: "Verification Status", class: "mb-0 fs-xs" }}
                    options={[
                        { value: "all", label: "All" },
                        { value: "pending", label: "Pending" },
                        { value: "in_progress", label: "In Progress" },
                        { value: "submitted", label: "Submitted" },
                        { value: "verified", label: "Verified" },
                        { value: "rejected", label: "Rejected" }
                    ]}
                    value={inlineVerification}
                    isClearable={false}
                    onChange={(selected) => {
                        const val = selected ? selected.value : "all";
                        setInlineVerification(val);
                        setAdvancedFilters(prev => ({ ...prev, verification_status: val === "all" ? "" : val }));
                        setCurrentPage(1);
                    }}
                    style={{ width: '150px' }}
                />

                {/* City */}
                <CustomSelect
                    options={mergedCityOptions}
                    value={selectedCities}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    selectProps={{
                        components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                        styles: customStyles
                    }}
                    placeholder="All"
                    onInputChange={handleCitySearch}
                    onChange={(selected) => {
                        const selectedArr = selected || [];
                        setSelectedCities(selectedArr);
                        setAdvancedFilters(prev => ({ ...prev, city_id: selectedArr.map(s => s.value) }));
                        setCurrentPage(1);
                    }}
                    label={{ name: "City", class: "mb-0 fs-xs" }}
                    style={{ width: "150px" }}
                />

                {/* Category */}
                <CustomSelect
                    options={mergedCategoryOptions}
                    value={selectedCategories}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    selectProps={{
                        components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                        styles: customStyles
                    }}
                    placeholder="All"
                    onInputChange={handleCategorySearch}
                    onChange={(selected) => {
                        const selectedArr = selected || [];
                        setSelectedCategories(selectedArr);
                        setAdvancedFilters(prev => ({ ...prev, category_id: selectedArr.map(s => s.value) }));
                        setCurrentPage(1);
                    }}
                    label={{ name: "Category", class: "mb-0" }}
                    style={{ width: "150px" }}
                />

                <InputField
                    style={{ width: '260px' }}
                    label={{ name: "Created Date", class: "mb-0 fs-xs" }}
                    input={{
                        type: "date-range",
                        value: createdDateRange,
                        placeholder: "Select date range",
                        onChange: (e) => {
                            const val = e.target.value;
                            setCreatedDateRange({ start: val?.start || null, end: val?.end || null });
                            setAdvancedFilters(prev => ({
                                ...prev,
                                start_date: val?.start || null,
                                end_date: val?.end || null,
                            }));
                            setCurrentPage(1);
                        },
                    }}
                />

                {/* Status */}
                <CustomSelect
                    label={{ name: "Status", class: "mb-0 fs-xs" }}
                    options={[
                        { value: "all", label: "All" },
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" }
                    ]}
                    value={inlineStatus}
                    isClearable={false}
                    onChange={(selected) => {
                        const val = selected ? selected.value : "all";
                        setInlineStatus(val);
                        setAdvancedFilters(prev => ({ ...prev, status: val === "all" ? "" : val }));
                        setCurrentPage(1);
                    }}
                    style={{ width: '150px' }}
                />

                {/* Buttons */}
                <div className="d-flex align-items-center gap-2 ms-auto filter-buttons">
                    {!isFilterActive && !searchTerm ? null : <ResetBtn onClick={handleResetFilters} />}
                    <FilterBtn onClick={handleOpenFilterDrawer} active={isFilterActive} count={filterCount} />
                </div>
            </div>

            <CustomTable
                data={data}
                columns={columns}
                keyField="id"
                actionList={actionList}
                module={MODULE}
                emptyMessage="No companies found."
                loading={isLoading && !isInitialLoad}
                rowClick={(item) => {
                    dispatch(setCompanyListSettings({ scrollPosition: window.scrollY }));
                    navigate(`/company/${item.id}`);
                }}
                tbody={{ trClass: "cursor-pointer" }}
                pagination={pagination}
            />

            <EditCompanyProfileModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSubmit={handleUpsertCompanyProfile}
                company={companyDetail}
                isLoading={updateCompanyLoading}
                title="Edit Company Profile"
                size="lg"
            />
        </>
    );
};

export default ListSection;
